import React, { useState, useEffect } from "react";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle 
} from "@/components/ui/dialog";
import { 
  Search, 
  FileText, 
  Clock, 
  User, 
  Mail, 
  Phone, 
  MapPin,
  Calendar,
  CheckCircle,
  XCircle,
  AlertCircle,
  Download,
  FileDown
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import {
  collection,
  query,
  where,
  getDocs,
  getFirestore,
  doc,
  updateDoc,
  serverTimestamp,
  deleteDoc,
  addDoc,
} from "firebase/firestore";
import { initializeApp } from "firebase/app";
import { firebaseConfig, isDemoMode } from "@/config/firebase";
import jsPDF from 'jspdf';

let db;
try {
  const app = initializeApp(firebaseConfig);
  db = getFirestore(app);
} catch (error) {
  console.error("Firebase initialization error:", error);
}

interface MockTestBooking {
  id: string;
  studentName: string;
  studentEmail: string;
  studentPhone: string;
  studentAddress: string;
  studentPostcode: string;
  courseName: string;
  date: string;
  time: string;
  status: 'pending' | 'completed' | 'cancelled';
  instructorId: string;
  instructorName: string;
  enrollmentId: string;
  createdAt: any;
  testResult?: {
    score?: number;
    totalQuestions?: number;
    passed?: boolean;
    notes?: string;
    completedAt?: any;
    // New comprehensive fields
    testDate?: string;
    testTime?: string;
    testCentre?: string;
    weather?: string;
    faults?: Record<string, { df: number; s: number; d: number }>;
    debriefNotes?: string;
    examinerSignature?: string;
  };
}

const MockTestResults = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [mockTestBookings, setMockTestBookings] = useState<MockTestBooking[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredBookings, setFilteredBookings] = useState<MockTestBooking[]>([]);
  const [selectedBooking, setSelectedBooking] = useState<MockTestBooking | null>(null);
  const [resultDialogOpen, setResultDialogOpen] = useState(false);
  const [testResult, setTestResult] = useState({
    score: 0,
    totalQuestions: 50,
    passed: false,
    notes: "",
    // New comprehensive fields
    testDate: new Date().toISOString().split('T')[0],
    testTime: new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }),
    testCentre: "",
    weather: "",
    faults: {} as Record<string, { df: number; s: number; d: number }>,
    debriefNotes: "",
    examinerSignature: ""
  });

  // Add print styles
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @media print {
        body * {
          visibility: hidden;
        }
        .print-section, .print-section * {
          visibility: visible;
        }
        .print-section {
          position: absolute;
          left: 0;
          top: 0;
          width: 100%;
        }
        .no-print {
          display: none !important;
        }
        .card {
          border: 1px solid #000 !important;
          box-shadow: none !important;
        }
        .table {
          border-collapse: collapse;
        }
        .table th, .table td {
          border: 1px solid #000 !important;
          padding: 8px !important;
        }
      }
    `;
    document.head.appendChild(style);
    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);

  useEffect(() => {
    fetchMockTestBookings();
  }, [user?.id]);

  useEffect(() => {
    filterBookings();
  }, [searchQuery, mockTestBookings]);

  const fetchMockTestBookings = async () => {
    if (!user?.id) {
      console.log("No user ID found");
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      console.log("Fetching mock test bookings for instructor:", user.id);

      if (isDemoMode || !db) {
        console.warn("Demo mode or Firestore not initialized - using mock data");
        const mockBookings: MockTestBooking[] = [
          {
            id: "1",
            studentName: "John Doe",
            studentEmail: "john.doe@example.com",
            studentPhone: "07123456789",
            studentAddress: "123 Main Street",
            studentPostcode: "SW1A 1AA",
            courseName: "Mock Test",
            date: "2025-01-20",
            time: "10:00",
            status: "pending",
            instructorId: user.id,
            instructorName: `${user.firstName || ''} ${user.lastName || ''}`.trim() || "Demo Instructor",
            enrollmentId: "enrollment-1",
            createdAt: new Date(),
          },
          {
            id: "2",
            studentName: "Jane Smith",
            studentEmail: "jane.smith@example.com",
            studentPhone: "07987654321",
            studentAddress: "456 Oak Avenue",
            studentPostcode: "W1A 1AA",
            courseName: "Mock Test",
            date: "2025-01-19",
            time: "14:00",
            status: "completed",
            instructorId: user.id,
            instructorName: `${user.firstName || ''} ${user.lastName || ''}`.trim() || "Demo Instructor",
            enrollmentId: "enrollment-2",
            createdAt: new Date(),
            testResult: {
              score: 45,
              totalQuestions: 50,
              passed: true,
              notes: "Excellent performance. Student shows good understanding of road rules.",
              completedAt: new Date()
            }
          },
          {
            id: "3",
            studentName: "Mike Johnson",
            studentEmail: "mike.johnson@example.com",
            studentPhone: "07555123456",
            studentAddress: "789 Pine Road",
            studentPostcode: "E1A 1AA",
            courseName: "Mock Test",
            date: "2025-01-18",
            time: "16:00",
            status: "completed",
            instructorId: user.id,
            instructorName: `${user.firstName || ''} ${user.lastName || ''}`.trim() || "Demo Instructor",
            enrollmentId: "enrollment-3",
            createdAt: new Date(),
            testResult: {
              score: 35,
              totalQuestions: 50,
              passed: false,
              notes: "Needs more practice with hazard perception and road signs.",
              completedAt: new Date()
            }
          }
        ];
        setMockTestBookings(mockBookings);
        setLoading(false);
        return;
      }

      // First, let's get ALL enrollments for this instructor to debug
      console.log("🔍 Step 1: Getting all enrollments for instructor:", user.id);
      const enrollmentsRef = collection(db, "enrollments");
      const allInstructorEnrollmentsQuery = query(
        enrollmentsRef,
        where("instructorId", "==", user.id)
      );
      
      const allEnrollmentsSnapshot = await getDocs(allInstructorEnrollmentsQuery);
      console.log(`📊 Found ${allEnrollmentsSnapshot.size} total enrollments for instructor ${user.id}`);
      
      // Log all enrollments to see what courses exist
      const allEnrollments = [];
      allEnrollmentsSnapshot.forEach((doc) => {
        const data = doc.data();
        allEnrollments.push({
          id: doc.id,
          courseName: data.courseName,
          studentName: data.studentName,
          status: data.status
        });
        console.log(`📋 Enrollment: ${data.courseName} - ${data.studentName} - ${data.status}`);
      });

      // Now look for mock test enrollments with flexible matching
      const mockTestBookings = [];
      
      allEnrollmentsSnapshot.forEach((doc) => {
        const data = doc.data();
        const courseName = data.courseName || '';
        
        // Check for various possible mock test course names
        const isMockTest = courseName.toLowerCase().includes('mock') || 
                          courseName.toLowerCase().includes('test') ||
                          courseName.toLowerCase() === 'mock test' ||
                          courseName.toLowerCase() === 'mocktest';
        
        if (isMockTest) {
          console.log(`✅ Found mock test enrollment: ${courseName} for student ${data.studentName}`);
          mockTestBookings.push({
            id: doc.id,
            studentName: data.studentName || "",
            studentEmail: data.studentEmail || "",
            studentPhone: data.studentPhone || "",
            studentAddress: data.studentAddress || "",
            studentPostcode: data.studentPostcode || "",
            courseName: data.courseName || "Mock Test",
            date: data.startDate || data.date || "",
            time: data.preferredTime || data.time || "",
            status: data.status || "pending",
            instructorId: data.instructorId || "",
            instructorName: data.instructorName || "",
            enrollmentId: doc.id,
            createdAt: data.createdAt,
            testResult: data.testResult || null
          });
        }
      });

      console.log(`🎯 Found ${mockTestBookings.length} mock test bookings for instructor ${user.id}`);
      
      if (mockTestBookings.length === 0) {
        console.log("⚠️ No mock test enrollments found. Available courses for this instructor:");
        const uniqueCourses = [...new Set(allEnrollments.map(e => e.courseName))];
        uniqueCourses.forEach(course => console.log(`   - ${course}`));
      }

      setMockTestBookings(mockTestBookings);
    } catch (error) {
      console.error("Error fetching mock test bookings:", error);
      toast({
        title: "Error",
        description: "Failed to fetch mock test bookings",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const filterBookings = () => {
    if (!searchQuery.trim()) {
      setFilteredBookings(mockTestBookings);
      return;
    }

    const filtered = mockTestBookings.filter((booking) =>
      booking.studentName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      booking.studentEmail.toLowerCase().includes(searchQuery.toLowerCase()) ||
      booking.studentPhone.includes(searchQuery)
    );
    setFilteredBookings(filtered);
  };

  const handleAddResult = (booking: MockTestBooking) => {
    setSelectedBooking(booking);
    
    // If editing existing result, preserve the data; otherwise use defaults
    if (booking.testResult) {
      setTestResult({
        score: booking.testResult.score || 0,
        totalQuestions: booking.testResult.totalQuestions || 50,
        passed: booking.testResult.passed || false,
        notes: booking.testResult.notes || "",
        // Preserve existing comprehensive fields or use defaults
        testDate: booking.testResult.testDate || new Date().toISOString().split('T')[0],
        testTime: booking.testResult.testTime || new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }),
        testCentre: booking.testResult.testCentre || "",
        weather: booking.testResult.weather || "",
        faults: booking.testResult.faults || {} as Record<string, { df: number; s: number; d: number }>,
        debriefNotes: booking.testResult.debriefNotes || "",
        examinerSignature: booking.testResult.examinerSignature || ""
      });
    } else {
      // New result - use defaults
      setTestResult({
        score: 0,
        totalQuestions: 50,
        passed: false,
        notes: "",
        // New comprehensive fields
        testDate: new Date().toISOString().split('T')[0],
        testTime: new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }),
        testCentre: "",
        weather: "",
        faults: {} as Record<string, { df: number; s: number; d: number }>,
        debriefNotes: "",
        examinerSignature: ""
      });
    }
    setResultDialogOpen(true);
  };

  const handleSaveResult = async () => {
    if (!selectedBooking) return;

    try {
      // Calculate pass/fail based on serious, dangerous faults, and driving faults
      const seriousFaults = Object.values(testResult.faults || {}).reduce((sum, fault) => sum + (fault.s || 0), 0);
      const dangerousFaults = Object.values(testResult.faults || {}).reduce((sum, fault) => sum + (fault.d || 0), 0);
      const drivingFaults = Object.values(testResult.faults || {}).reduce((sum, fault) => sum + (fault.df || 0), 0);
      
      // New pass/fail logic: FAIL if any serious faults, dangerous faults, or 4+ driving faults
      const passed = seriousFaults === 0 && dangerousFaults === 0 && drivingFaults < 4;
      
      const updatedResult = {
        ...testResult,
        passed,
        completedAt: serverTimestamp()
      };

      if (isDemoMode || !db) {
        // Update local state for demo mode
        setMockTestBookings(prev => prev.map(booking => 
          booking.id === selectedBooking.id 
            ? { ...booking, status: 'completed', testResult: updatedResult }
            : booking
        ));
        toast({
          title: "Success",
          description: "Test result saved successfully",
        });
        setResultDialogOpen(false);
        return;
      }

      // Update the enrollment document
      const enrollmentRef = doc(db, "enrollments", selectedBooking.enrollmentId);
      await updateDoc(enrollmentRef, {
        status: "completed",
        testResult: updatedResult,
        updatedAt: serverTimestamp()
      });

      // Create a separate result record in the "results" collection
      const resultData = {
        enrollmentId: selectedBooking.enrollmentId,
        studentId: selectedBooking.studentName,
        studentEmail: selectedBooking.studentEmail,
        studentPhone: selectedBooking.studentPhone,
        instructorId: selectedBooking.instructorId,
        instructorName: selectedBooking.instructorName,
        testDate: testResult.testDate,
        testTime: testResult.testTime,
        testCentre: testResult.testCentre,
        weather: testResult.weather,
        faults: testResult.faults,
        debriefNotes: testResult.debriefNotes,
        examinerSignature: testResult.examinerSignature,
        passed: passed,
        totalDrivingFaults: Object.values(testResult.faults || {}).reduce((sum, fault) => sum + (fault.df || 0), 0),
        totalSeriousFaults: seriousFaults,
        totalDangerousFaults: dangerousFaults,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const resultsRef = collection(db, "results");
      await addDoc(resultsRef, resultData);

      // Update local state
      setMockTestBookings(prev => prev.map(booking => 
        booking.id === selectedBooking.id 
          ? { ...booking, status: 'completed', testResult: updatedResult }
          : booking
      ));

      toast({
        title: "Success",
        description: "Test result saved successfully",
      });
      setResultDialogOpen(false);
    } catch (error) {
      console.error("Error saving test result:", error);
      toast({
        title: "Error",
        description: "Failed to save test result",
        variant: "destructive",
      });
    }
  };

  const handleDeleteBooking = async (booking: MockTestBooking) => {
    if (!isDemoMode && db) {
      try {
        const enrollmentRef = doc(db, "enrollments", booking.enrollmentId);
        await deleteDoc(enrollmentRef);
        toast({
          title: "Success",
          description: "Booking deleted successfully.",
        });
        setMockTestBookings(prev => prev.filter(b => b.id !== booking.id));
      } catch (error) {
        console.error("Error deleting booking:", error);
        toast({
          title: "Error",
          description: "Failed to delete booking.",
          variant: "destructive",
        });
      }
    } else {
      toast({
        title: "Demo Mode",
        description: "You are in demo mode. Cannot delete bookings directly.",
      });
    }
  };

  const generatePDF = (booking: MockTestBooking) => {
    try {
      console.log('Starting PDF generation for:', booking.studentName);
      
      // Validate booking data
      if (!booking || !booking.studentName) {
        throw new Error('Invalid booking data');
      }
      
      const doc = new jsPDF();
      
      // Set page margins and dimensions
      const pageWidth = doc.internal.pageSize.width;
      const pageHeight = doc.internal.pageSize.height;
      const margin = 20;
      const contentWidth = pageWidth - (2 * margin);
      
      // Header with logo and company info
      doc.setFillColor(41, 128, 185); // Blue background
      doc.rect(0, 0, pageWidth, 45, 'F'); // Increased height
      
      // Add a subtle gradient effect with a darker line
      doc.setFillColor(31, 97, 141); // Darker blue
      doc.rect(0, 43, pageWidth, 2, 'F'); // Adjusted position
      
      // Company name in header
      doc.setFontSize(18); // Slightly smaller for better fit
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(255, 255, 255);
      doc.text('Way2Learn Driving School', pageWidth / 2, 12, { align: 'center' });
      
      // Subtitle
      doc.setFontSize(11); // Slightly smaller
      doc.setFont('helvetica', 'normal');
      doc.text('Professional Driving Test Marking Sheet', pageWidth / 2, 24, { align: 'center' });
      
      // Contact info with better formatting and spacing
      doc.setFontSize(7); // Smaller font for contact info
      doc.setFont('helvetica', 'bold');
      doc.text('Phone: 020 3488 9598', pageWidth / 2 - 45, 35, { align: 'center' });
      doc.text('|', pageWidth / 2, 35, { align: 'center' });
      doc.text('Website: way2learndrivingschool.com', pageWidth / 2 + 45, 35, { align: 'center' });
      
      // Reset text color for content
      doc.setTextColor(0, 0, 0);
      
      let yPos = 65; // Increased starting position
      
      // Candidate Details Section
      doc.setFillColor(236, 240, 241); // Light gray background
      doc.rect(margin, yPos - 5, contentWidth, 30, 'F'); // Increased height
      
      // Add border
      doc.setDrawColor(189, 195, 199);
      doc.rect(margin, yPos - 5, contentWidth, 30, 'S');
      
      doc.setFontSize(13); // Slightly smaller
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(52, 73, 94); // Dark blue text
      doc.text('Candidate Details', margin + 5, yPos);
      
      // Add a subtle line under the title
      doc.setDrawColor(52, 152, 219);
      doc.line(margin + 5, yPos + 2, margin + 80, yPos + 2);
      
      // Candidate details in two columns with better formatting
      const detailsLeft = [
        `Name: ${booking.studentName || 'N/A'}`,
        `Date: ${booking.testResult?.testDate || booking.date || 'N/A'}`,
        `Time: ${booking.testResult?.testTime || 'N/A'}`
      ];
      
      const detailsRight = [
        `Instructor: ${booking.instructorName || 'N/A'}`,
        `Test Centre: ${booking.testResult?.testCentre || 'N/A'}`,
        `Weather: ${booking.testResult?.weather || 'N/A'}`
      ];
      
      doc.setFontSize(9); // Slightly smaller
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      
      detailsLeft.forEach((detail, index) => {
        doc.text(detail, margin + 5, yPos + 15 + (index * 6)); // Increased spacing
      });
      
      detailsRight.forEach((detail, index) => {
        doc.text(detail, margin + contentWidth / 2, yPos + 15 + (index * 6)); // Increased spacing
      });
      
      yPos += 45; // Increased spacing
      
      // Fault Marking Section Header
      doc.setFillColor(52, 73, 94); // Dark blue background
      doc.rect(margin, yPos - 5, contentWidth, 15, 'F');
      
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(255, 255, 255);
      doc.text('Fault Marking Assessment', margin + 5, yPos + 5);
      
      yPos += 20;
      
      // Table headers
      doc.setFillColor(189, 195, 199); // Light gray for headers
      doc.rect(margin, yPos - 5, contentWidth, 12, 'F');
      
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 0, 0);
      doc.text('Competency', margin + 5, yPos + 3);
      doc.text('DF', margin + contentWidth - 60, yPos + 3);
      doc.text('S', margin + contentWidth - 40, yPos + 3);
      doc.text('D', margin + contentWidth - 20, yPos + 3);
      
      yPos += 15;
      
      // Define competencies with groups
      const competencyGroups = [
        {
          name: 'Eyesight and Basic Controls',
          competencies: ['Eyesight Check', 'Controlled Stop']
        },
        {
          name: 'Moving Off',
          competencies: ['Move off - Safety', 'Move off - Control']
        },
        {
          name: 'Use of Mirrors',
          competencies: ['Use of mirrors - Change direction', 'Use of mirrors - Change speed', 'Use of mirrors signalling']
        },
        {
          name: 'Signals',
          competencies: ['Signals - Necessary', 'Signals - Correctly', 'Signal timed']
        },
        {
          name: 'Clearance and Following Distance',
          competencies: ['Clearance / Obstructions', 'Following distance']
        },
        {
          name: 'Response to Signs and Signals',
          competencies: ['Response to signs - Traffic signs', 'Response to signs - Road markings', 'Response to signals - Traffic lights']
        },
        {
          name: 'Junctions',
          competencies: ['Junctions - Approach speed', 'Junctions - Observation']
        },
        {
          name: 'Judgement',
          competencies: ['Judgement - Meeting', 'Judgement - Crossing']
        },
        {
          name: 'Positioning',
          competencies: ['Positioning - Normal driving', 'Positioning - Lane discipline']
        },
        {
          name: 'Pedestrian Crossings and Stops',
          competencies: ['Pedestrian crossings', 'Position / Normal stops']
        },
        {
          name: 'Vehicle Control',
          competencies: ['Control - Steering', 'Control - Gears', 'Control - Accelerator', 'Control - Footbrake', 'Control - Clutch']
        },
        {
          name: 'Use of Speed',
          competencies: ['Use of speed']
        },
        {
          name: 'Progress',
          competencies: ['Progress - Appropriate', 'Progress - Speed', 'Progress - Under hesitation']
        },
        {
          name: 'Emergency Stop and Parking',
          competencies: ['Emergency stop', 'Reverse park - Road', 'Reverse park - Bay', 'Forward park', 'Reverse / Right']
        },
        {
          name: 'Show Me Tell Me',
          competencies: ['Show Me / Tell Me']
        }
      ];
      
      console.log('Processing competency groups...');
      
      // Table content with alternating row colors
      let rowCount = 0;
      
      competencyGroups.forEach((group, groupIndex) => {
        // Check if we need a new page
        if (yPos > pageHeight - 80) {
          doc.addPage();
          yPos = 20;
        }
        
        // Group header with colored background
        doc.setFillColor(52, 152, 219); // Blue background for group headers
        doc.rect(margin, yPos - 3, contentWidth, 10, 'F');
        
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(255, 255, 255);
        doc.text(group.name, margin + 5, yPos + 3);
        
        yPos += 12;
        
        // Add competencies in group
        group.competencies.forEach((competency, index) => {
          if (yPos > pageHeight - 80) {
            doc.addPage();
            yPos = 20;
          }
          
          // Alternating row colors
          if (rowCount % 2 === 0) {
            doc.setFillColor(248, 249, 250); // Very light gray
            doc.rect(margin, yPos - 2, contentWidth, 8, 'F');
          }
          
          const faults = booking.testResult?.faults?.[competency] || { df: 0, s: 0, d: 0 };
          
          doc.setFontSize(8);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(0, 0, 0);
          
          // Competency name (truncated if too long)
          const maxWidth = contentWidth - 80;
          let competencyText = competency;
          if (doc.getTextWidth(competencyText) > maxWidth) {
            while (doc.getTextWidth(competencyText + '...') > maxWidth && competencyText.length > 0) {
              competencyText = competencyText.slice(0, -1);
            }
            competencyText += '...';
          }
          doc.text(competencyText, margin + 5, yPos + 3);
          
          // Fault values with colored backgrounds for non-zero values
          const dfValue = (faults.df || 0).toString();
          const sValue = (faults.s || 0).toString();
          const dValue = (faults.d || 0).toString();
          
          // DF column
          if (faults.df > 0) {
            doc.setFillColor(255, 193, 7); // Yellow for DF
            doc.rect(margin + contentWidth - 60, yPos - 2, 15, 8, 'F');
          }
          doc.text(dfValue, margin + contentWidth - 52, yPos + 3);
          
          // S column
          if (faults.s > 0) {
            doc.setFillColor(255, 152, 0); // Orange for S
            doc.rect(margin + contentWidth - 40, yPos - 2, 15, 8, 'F');
          }
          doc.text(sValue, margin + contentWidth - 32, yPos + 3);
          
          // D column
          if (faults.d > 0) {
            doc.setFillColor(244, 67, 54); // Red for D
            doc.rect(margin + contentWidth - 20, yPos - 2, 15, 8, 'F');
          }
          doc.text(dValue, margin + contentWidth - 12, yPos + 3);
          
          yPos += 10;
          rowCount++;
        });
        
        yPos += 3; // Space between groups
      });
      
      // Summary section on new page
      if (yPos > pageHeight - 120) {
        doc.addPage();
        yPos = 20;
      }
      
      // Summary header
      doc.setFillColor(46, 204, 113); // Green background
      doc.rect(margin, yPos - 5, contentWidth, 20, 'F');
      
      // Add a subtle gradient effect
      doc.setFillColor(39, 174, 96); // Darker green
      doc.rect(margin, yPos - 5, contentWidth, 2, 'F');
      
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(255, 255, 255);
      doc.text('Test Result Summary', margin + 5, yPos + 8);
      
      yPos += 25;
      
      // Safe calculation of totals with null checks
      const totalDF = Object.values(booking.testResult?.faults || {}).reduce((sum, fault) => {
        return sum + (fault?.df || 0);
      }, 0);
      const totalS = Object.values(booking.testResult?.faults || {}).reduce((sum, fault) => {
        return sum + (fault?.s || 0);
      }, 0);
      const totalD = Object.values(booking.testResult?.faults || {}).reduce((sum, fault) => {
        return sum + (fault?.d || 0);
      }, 0);
      const passed = totalS === 0 && totalD === 0 && totalDF < 4;
      
      // Summary grid with enhanced styling
      const summaryData = [
        { label: 'Total Driving Faults (DF)', value: totalDF, color: totalDF >= 4 ? [244, 67, 54] : [52, 152, 219] },
        { label: 'Total Serious Faults (S)', value: totalS, color: totalS > 0 ? [244, 67, 54] : [255, 152, 0] },
        { label: 'Total Dangerous Faults (D)', value: totalD, color: totalD > 0 ? [244, 67, 54] : [244, 67, 54] },
        { label: 'Test Result', value: passed ? 'PASS' : 'FAIL', color: passed ? [46, 204, 113] : [244, 67, 54] }
      ];
      
      summaryData.forEach((item, index) => {
        const x = margin + (index % 2) * (contentWidth / 2);
        const y = yPos + (Math.floor(index / 2) * 25);
        
        // Enhanced background with gradient effect
        doc.setFillColor(248, 249, 250);
        doc.rect(x, y - 3, contentWidth / 2 - 5, 20, 'F');
        
        // Enhanced border
        doc.setDrawColor(189, 195, 199);
        doc.setLineWidth(0.5);
        doc.rect(x, y - 3, contentWidth / 2 - 5, 20, 'S');
        
        // Add subtle shadow effect
        doc.setFillColor(240, 240, 240);
        doc.rect(x + 1, y - 2, contentWidth / 2 - 5, 20, 'F');
        doc.setFillColor(248, 249, 250);
        doc.rect(x, y - 3, contentWidth / 2 - 5, 20, 'F');
        doc.setDrawColor(189, 195, 199);
        doc.rect(x, y - 3, contentWidth / 2 - 5, 20, 'S');
        
        // Label
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(52, 73, 94);
        doc.text(item.label, x + 5, y + 5);
        
        // Value with enhanced styling
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(item.color[0], item.color[1], item.color[2]);
        doc.text(item.value.toString(), x + 5, y + 15);
      });
      
      yPos += 60;
      
      // Pass/Fail Rules
      doc.setFillColor(236, 240, 241);
      doc.rect(margin, yPos - 5, contentWidth, 35, 'F');
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(52, 73, 94);
      doc.text('Pass/Fail Rules:', margin + 5, yPos + 5);
      
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      doc.text('• FAIL if any Serious Faults (S) > 0', margin + 5, yPos + 15);
      doc.text('• FAIL if any Dangerous Faults (D) > 0', margin + 5, yPos + 22);
      doc.text('• FAIL if Driving Faults (DF) ≥ 4', margin + 5, yPos + 29);
      
      yPos += 45;
      
      // Debrief Notes
      if (booking.testResult?.debriefNotes) {
        doc.setFillColor(255, 255, 255);
        doc.rect(margin, yPos - 5, contentWidth, 30, 'F');
        doc.setDrawColor(189, 195, 199);
        doc.rect(margin, yPos - 5, contentWidth, 30, 'S');
        
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(52, 73, 94);
        doc.text('Debrief Notes:', margin + 5, yPos + 5);
        
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(0, 0, 0);
        const notes = doc.splitTextToSize(booking.testResult.debriefNotes, contentWidth - 10);
        doc.text(notes, margin + 5, yPos + 15);
      }
      
      // Footer with enhanced styling
      const footerY = pageHeight - 25;
      
      // Footer background
      doc.setFillColor(52, 73, 94);
      doc.rect(0, footerY - 5, pageWidth, 25, 'F');
      
      // Footer text
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(255, 255, 255);
      doc.text(`Generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}`, pageWidth / 2, footerY + 5, { align: 'center' });
      
      // Add company info in footer
      doc.text('Way2Learn Driving School - Professional Driving Assessment', pageWidth / 2, footerY + 12, { align: 'center' });
      
      // Add page number
      doc.text(`Page ${doc.getCurrentPageInfo().pageNumber}`, pageWidth - margin, footerY + 5, { align: 'right' });
      
      console.log('PDF generation completed, saving file...');
      
      // Generate filename
      const filename = `driving-test-${booking.studentName.replace(/[^a-zA-Z0-9]/g, '-')}-${booking.testResult?.testDate || booking.date}.pdf`;
      
      // Save PDF
      doc.save(filename);
      
      console.log('PDF saved successfully:', filename);
      
      // Show success toast
      toast({
        title: "PDF Generated",
        description: `PDF saved as ${filename}`,
      });
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast({
        title: "PDF Generation Failed",
        description: "There was an error generating the PDF. Please try again.",
        variant: "destructive",
      });
    }
  };

  const generateWord = (booking: MockTestBooking) => {
    // Define competencies with groups
    const competencyGroups = [
      {
        name: 'Eyesight and Basic Controls',
        competencies: ['Eyesight Check', 'Controlled Stop']
      },
      {
        name: 'Moving Off',
        competencies: ['Move off - Safety', 'Move off - Control']
      },
      {
        name: 'Use of Mirrors',
        competencies: ['Use of mirrors - Change direction', 'Use of mirrors - Change speed', 'Use of mirrors signalling']
      },
      {
        name: 'Signals',
        competencies: ['Signals - Necessary', 'Signals - Correctly', 'Signal timed']
      },
      {
        name: 'Clearance and Following Distance',
        competencies: ['Clearance / Obstructions', 'Following distance']
      },
      {
        name: 'Response to Signs and Signals',
        competencies: ['Response to signs - Traffic signs', 'Response to signs - Road markings', 'Response to signals - Traffic lights']
      },
      {
        name: 'Junctions',
        competencies: ['Junctions - Approach speed', 'Junctions - Observation']
      },
      {
        name: 'Judgement',
        competencies: ['Judgement - Meeting', 'Judgement - Crossing']
      },
      {
        name: 'Positioning',
        competencies: ['Positioning - Normal driving', 'Positioning - Lane discipline']
      },
      {
        name: 'Pedestrian Crossings and Stops',
        competencies: ['Pedestrian crossings', 'Position / Normal stops']
      },
      {
        name: 'Vehicle Control',
        competencies: ['Control - Steering', 'Control - Gears', 'Control - Accelerator', 'Control - Footbrake', 'Control - Clutch']
      },
      {
        name: 'Use of Speed',
        competencies: ['Use of speed']
      },
      {
        name: 'Progress',
        competencies: ['Progress - Appropriate', 'Progress - Speed', 'Progress - Under hesitation']
      },
      {
        name: 'Emergency Stop and Parking',
        competencies: ['Emergency stop', 'Reverse park - Road', 'Reverse park - Bay', 'Forward park', 'Reverse / Right']
      },
      {
        name: 'Show Me Tell Me',
        competencies: ['Show Me / Tell Me']
      }
    ];

    // Calculate totals
    const totalDF = Object.values(booking.testResult?.faults || {}).reduce((sum, fault) => sum + (fault.df || 0), 0);
    const totalS = Object.values(booking.testResult?.faults || {}).reduce((sum, fault) => sum + (fault.s || 0), 0);
    const totalD = Object.values(booking.testResult?.faults || {}).reduce((sum, fault) => sum + (fault.d || 0), 0);
    const passed = totalS === 0 && totalD === 0 && totalDF < 4;

    // Create HTML content for Word document with enhanced styling
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Driving Test Marking Sheet</title>
        <style>
          body { 
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; 
            margin: 0; 
            padding: 20px; 
            background-color: #f8f9fa;
            color: #333;
          }
          .container {
            max-width: 800px;
            margin: 0 auto;
            background: white;
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
            border-radius: 8px;
            overflow: hidden;
          }
          .header { 
            background: linear-gradient(135deg, #2980b9 0%, #3498db 100%);
            color: white;
            padding: 30px;
            text-align: center;
            position: relative;
          }
          .header::after {
            content: '';
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            height: 3px;
            background: linear-gradient(90deg, #e74c3c, #f39c12, #27ae60);
          }
          .title { 
            font-size: 28px; 
            font-weight: bold; 
            margin-bottom: 8px;
            text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
          }
          .subtitle { 
            font-size: 16px; 
            margin-bottom: 15px;
            opacity: 0.9;
          }
          .contact-info {
            display: flex;
            justify-content: center;
            gap: 30px;
            font-size: 14px;
            opacity: 0.8;
          }
          .contact-item {
            display: flex;
            align-items: center;
            gap: 8px;
          }
          .section { 
            margin: 25px 0; 
            padding: 0 30px;
          }
          .section-title { 
            font-size: 18px; 
            font-weight: bold; 
            margin-bottom: 15px;
            color: #2c3e50;
            border-bottom: 3px solid #3498db;
            padding-bottom: 8px;
          }
          .details-grid { 
            display: grid; 
            grid-template-columns: 1fr 1fr; 
            gap: 20px; 
            margin-bottom: 20px;
            background: #f8f9fa;
            padding: 20px;
            border-radius: 8px;
            border-left: 4px solid #3498db;
          }
          .detail-item { 
            margin-bottom: 12px;
            display: flex;
            align-items: center;
          }
          .label { 
            font-weight: bold; 
            color: #2c3e50;
            min-width: 120px;
          }
          .value {
            color: #34495e;
            font-weight: 500;
          }
          table { 
            width: 100%; 
            border-collapse: collapse; 
            margin: 20px 0;
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
            border-radius: 8px;
            overflow: hidden;
          }
          th, td { 
            border: 1px solid #ddd; 
            padding: 12px 8px; 
            text-align: left; 
          }
          th { 
            background: linear-gradient(135deg, #34495e 0%, #2c3e50 100%);
            color: white;
            font-weight: bold;
            text-align: center;
          }
          .group-header { 
            background: linear-gradient(135deg, #3498db 0%, #2980b9 100%);
            color: white;
            font-weight: bold;
            padding: 12px;
            text-align: center;
            font-size: 14px;
          }
          .fault-cell {
            text-align: center;
            font-weight: bold;
          }
          .fault-df { background-color: #fff3cd; }
          .fault-s { background-color: #ffeaa7; }
          .fault-d { background-color: #fab1a0; }
          .summary-grid { 
            display: grid; 
            grid-template-columns: 1fr 1fr; 
            gap: 20px;
            margin: 20px 0;
          }
          .summary-item {
            background: linear-gradient(135deg, #ecf0f1 0%, #bdc3c7 100%);
            padding: 20px;
            border-radius: 8px;
            text-align: center;
            border: 2px solid #bdc3c7;
            position: relative;
            overflow: hidden;
          }
          .summary-item::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            height: 4px;
            background: linear-gradient(90deg, #3498db, #2980b9);
          }
          .summary-label { 
            font-weight: bold;
            color: #2c3e50;
            margin-bottom: 8px;
            font-size: 14px;
          }
          .summary-value { 
            font-size: 24px; 
            font-weight: bold;
            margin-bottom: 5px;
          }
          .result-pass { color: #27ae60; }
          .result-fail { color: #e74c3c; }
          .result-df { color: #f39c12; }
          .result-s { color: #e67e22; }
          .result-d { color: #c0392b; }
          .rules-section {
            background: linear-gradient(135deg, #ecf0f1 0%, #bdc3c7 100%);
            padding: 20px;
            border-radius: 8px;
            margin: 20px 0;
            border-left: 4px solid #e74c3c;
          }
          .rules-title {
            font-weight: bold;
            color: #2c3e50;
            margin-bottom: 15px;
            font-size: 16px;
          }
          .rules-list {
            list-style: none;
            padding: 0;
          }
          .rules-list li {
            margin-bottom: 8px;
            padding-left: 20px;
            position: relative;
          }
          .rules-list li::before {
            content: '•';
            color: #e74c3c;
            font-weight: bold;
            position: absolute;
            left: 0;
          }
          .fail-rule { color: #e74c3c; font-weight: bold; }
          .pass-rule { color: #27ae60; font-weight: bold; }
          .debrief-section {
            background: #f8f9fa;
            padding: 20px;
            border-radius: 8px;
            border-left: 4px solid #27ae60;
            margin: 20px 0;
          }
          .footer {
            background: linear-gradient(135deg, #34495e 0%, #2c3e50 100%);
            color: white;
            padding: 20px;
            text-align: center;
            margin-top: 30px;
          }
          .footer-info {
            display: flex;
            justify-content: space-between;
            align-items: center;
            font-size: 12px;
            opacity: 0.8;
          }
          @media print {
            body { margin: 0; background: white; }
            .container { box-shadow: none; }
          }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <div class="title">Way2Learn Driving School</div>
            <div class="subtitle">Professional Driving Test Marking Sheet</div>
            <div class="contact-info">
              <div class="contact-item">
                <span>📞</span>
                <span>Phone: 020 3488 9598</span>
              </div>
              <div class="contact-item">
                <span>🌐</span>
                <span>Website: way2learndrivingschool.com</span>
              </div>
            </div>
          </div>
          
          <div class="section">
            <div class="section-title">Candidate Details</div>
            <div class="details-grid">
              <div class="detail-item">
                <span class="label">Name:</span>
                <span class="value">${booking.studentName}</span>
              </div>
              <div class="detail-item">
                <span class="label">Date:</span>
                <span class="value">${booking.testResult?.testDate || booking.date}</span>
              </div>
              <div class="detail-item">
                <span class="label">Time:</span>
                <span class="value">${booking.testResult?.testTime || 'N/A'}</span>
              </div>
              <div class="detail-item">
                <span class="label">Instructor:</span>
                <span class="value">${booking.instructorName}</span>
              </div>
              <div class="detail-item">
                <span class="label">Test Centre:</span>
                <span class="value">${booking.testResult?.testCentre || 'N/A'}</span>
              </div>
              <div class="detail-item">
                <span class="label">Weather:</span>
                <span class="value">${booking.testResult?.weather || 'N/A'}</span>
              </div>
            </div>
          </div>
          
          <div class="section">
            <div class="section-title">Fault Marking Assessment</div>
            <table>
              <thead>
                <tr>
                  <th>Competency</th>
                  <th>DF</th>
                  <th>S</th>
                  <th>D</th>
                </tr>
              </thead>
              <tbody>
                ${competencyGroups.map(group => `
                  <tr>
                    <td colspan="4" class="group-header">${group.name}</td>
                  </tr>
                  ${group.competencies.map(competency => {
                    const faults = booking.testResult?.faults?.[competency] || { df: 0, s: 0, d: 0 };
                    const dfClass = faults.df > 0 ? 'fault-df' : '';
                    const sClass = faults.s > 0 ? 'fault-s' : '';
                    const dClass = faults.d > 0 ? 'fault-d' : '';
                    return `<tr>
                      <td>${competency}</td>
                      <td class="fault-cell ${dfClass}">${faults.df || 0}</td>
                      <td class="fault-cell ${sClass}">${faults.s || 0}</td>
                      <td class="fault-cell ${dClass}">${faults.d || 0}</td>
                    </tr>`;
                  }).join('')}
                `).join('')}
              </tbody>
            </table>
          </div>
          
          <div class="section">
            <div class="section-title">Test Result Summary</div>
            <div class="summary-grid">
              <div class="summary-item">
                <div class="summary-label">Total Driving Faults (DF)</div>
                <div class="summary-value result-df">${totalDF}</div>
                ${totalDF >= 4 ? '<div style="color: #e74c3c; font-weight: bold;">FAIL - Too many DF</div>' : ''}
              </div>
              <div class="summary-item">
                <div class="summary-label">Total Serious Faults (S)</div>
                <div class="summary-value result-s">${totalS}</div>
                ${totalS > 0 ? '<div style="color: #e74c3c; font-weight: bold;">FAIL - Serious fault</div>' : ''}
              </div>
              <div class="summary-item">
                <div class="summary-label">Total Dangerous Faults (D)</div>
                <div class="summary-value result-d">${totalD}</div>
                ${totalD > 0 ? '<div style="color: #e74c3c; font-weight: bold;">FAIL - Dangerous fault</div>' : ''}
              </div>
              <div class="summary-item">
                <div class="summary-label">Test Result</div>
                <div class="summary-value ${passed ? 'result-pass' : 'result-fail'}">${passed ? 'PASS' : 'FAIL'}</div>
                <div style="color: ${passed ? '#27ae60' : '#e74c3c'}; font-weight: bold;">
                  ${passed ? 'Congratulations!' : 'Needs improvement'}
                </div>
              </div>
            </div>
          </div>
          
          <div class="rules-section">
            <div class="rules-title">Pass/Fail Rules</div>
            <ul class="rules-list">
              <li class="fail-rule">FAIL if any Serious Faults (S) > 0</li>
              <li class="fail-rule">FAIL if any Dangerous Faults (D) > 0</li>
              <li class="fail-rule">FAIL if Driving Faults (DF) ≥ 4</li>
              <li class="pass-rule">PASS only if S = 0, D = 0, and DF < 4</li>
            </ul>
          </div>
          
          ${booking.testResult?.debriefNotes ? `
          <div class="section">
            <div class="section-title">Debrief Notes</div>
            <div class="debrief-section">
              <div style="white-space: pre-wrap; line-height: 1.6;">${booking.testResult.debriefNotes}</div>
            </div>
          </div>
          ` : ''}
          
          <div class="footer">
            <div class="footer-info">
              <div>Generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}</div>
              <div>Way2Learn Driving School - Professional Driving Assessment</div>
            </div>
          </div>
        </div>
      </body>
      </html>
    `;
    
    // Create blob and download
    const blob = new Blob([htmlContent], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `driving-test-${booking.studentName.replace(/[^a-zA-Z0-9]/g, '-')}-${booking.testResult?.testDate || booking.date}.docx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge variant="secondary"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
      case 'completed':
        return <Badge variant="default"><CheckCircle className="w-3 h-3 mr-1" />Completed</Badge>;
      case 'cancelled':
        return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" />Cancelled</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getResultBadge = (testResult: any) => {
    if (!testResult) return null;
    
    if (testResult.passed) {
      return <Badge variant="default" className="bg-green-600">Passed</Badge>;
    } else {
      return <Badge variant="destructive">Failed</Badge>;
    }
  };

  // Add fallback PDF generation
  const generatePDFFallback = (booking: MockTestBooking) => {
    try {
      // Create a simple HTML-based PDF alternative
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <title>Driving Test Marking Sheet</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .header { text-align: center; margin-bottom: 30px; }
            .title { font-size: 18px; font-weight: bold; margin-bottom: 5px; }
            .subtitle { font-size: 12px; color: #666; }
            .section { margin-bottom: 20px; }
            .section-title { font-size: 14px; font-weight: bold; margin-bottom: 10px; }
            .details { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 15px; }
            .detail-item { margin-bottom: 5px; }
            .label { font-weight: bold; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f5f5f5; font-weight: bold; }
            .group-header { background-color: #e8f4fd; font-weight: bold; padding: 10px; border: 1px solid #ddd; }
            .summary { background-color: #f0f8ff; padding: 15px; border-radius: 5px; }
            .summary-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; }
            .result { font-size: 16px; font-weight: bold; }
            .pass { color: green; }
            .fail { color: red; }
            @media print {
              body { margin: 0; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="title">Way2Learn Driving School - Driving Test Marking Sheet</div>
            <div class="subtitle">�� 020 3488 9598 | 🌐 www.way2learndrivingschool.com</div>
          </div>
          
          <div class="section">
            <div class="section-title">Candidate Details</div>
            <div class="details">
              <div class="detail-item"><span class="label">Name:</span> ${booking.studentName}</div>
              <div class="detail-item"><span class="label">Date:</span> ${booking.testResult?.testDate || booking.date}</div>
              <div class="detail-item"><span class="label">Time:</span> ${booking.testResult?.testTime || 'N/A'}</div>
              <div class="detail-item"><span class="label">Instructor:</span> ${booking.instructorName}</div>
              <div class="detail-item"><span class="label">Test Centre:</span> ${booking.testResult?.testCentre || 'N/A'}</div>
              <div class="detail-item"><span class="label">Weather:</span> ${booking.testResult?.weather || 'N/A'}</div>
            </div>
          </div>
          
          <div class="section">
            <div class="section-title">Test Result Summary</div>
            <div class="summary">
              <div class="summary-grid">
                <div><span class="label">Total Driving Faults (DF):</span> ${Object.values(booking.testResult?.faults || {}).reduce((sum, fault) => sum + (fault.df || 0), 0)}</div>
                <div><span class="label">Total Serious Faults (S):</span> ${Object.values(booking.testResult?.faults || {}).reduce((sum, fault) => sum + (fault.s || 0), 0)}</div>
                <div><span class="label">Total Dangerous Faults (D):</span> ${Object.values(booking.testResult?.faults || {}).reduce((sum, fault) => sum + (fault.d || 0), 0)}</div>
                <div><span class="label">Test Result:</span> <span class="result ${(() => {
                  const totalS = Object.values(booking.testResult?.faults || {}).reduce((sum, fault) => sum + (fault.s || 0), 0);
                  const totalD = Object.values(booking.testResult?.faults || {}).reduce((sum, fault) => sum + (fault.d || 0), 0);
                  return totalS === 0 && totalD === 0 ? 'pass' : 'fail';
                })()}">${(() => {
                  const totalS = Object.values(booking.testResult?.faults || {}).reduce((sum, fault) => sum + (fault.s || 0), 0);
                  const totalD = Object.values(booking.testResult?.faults || {}).reduce((sum, fault) => sum + (fault.d || 0), 0);
                  return totalS === 0 && totalD === 0 ? 'PASS' : 'FAIL';
                })()}</span></div>
              </div>
            </div>
          </div>
          
          ${booking.testResult?.debriefNotes ? `
          <div class="section">
            <div class="section-title">Debrief Notes</div>
            <div>${booking.testResult.debriefNotes}</div>
          </div>
          ` : ''}
          
          <div class="no-print" style="text-align: center; margin-top: 30px;">
            <button onclick="window.print()" style="padding: 10px 20px; background: #007bff; color: white; border: none; border-radius: 5px; cursor: pointer;">
              Print / Save as PDF
            </button>
          </div>
        </body>
        </html>
      `;
      
      // Open in new window for printing
      const newWindow = window.open('', '_blank');
      if (newWindow) {
        newWindow.document.write(htmlContent);
        newWindow.document.close();
        newWindow.focus();
      } else {
        // Fallback: create blob and download
        const blob = new Blob([htmlContent], { type: 'text/html' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `driving-test-${booking.studentName.replace(/[^a-zA-Z0-9]/g, '-')}-${booking.testResult?.testDate || booking.date}.html`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
      
    } catch (error) {
      console.error('Error in PDF fallback:', error);
      toast({
        title: "PDF Generation Failed",
        description: "Unable to generate PDF. Please try the Word download instead.",
        variant: "destructive",
      });
    }
  };

  // Add simple PDF test function
  const testPDFGeneration = () => {
    try {
      console.log('Testing PDF generation...');
      const doc = new jsPDF();
      doc.text('Test PDF Generation', 20, 20);
      doc.text('If you can see this, PDF generation is working!', 20, 30);
      doc.save('test-pdf-generation.pdf');
      console.log('Test PDF generated successfully');
      toast({
        title: "PDF Test Successful",
        description: "PDF generation is working correctly.",
      });
    } catch (error) {
      console.error('PDF test failed:', error);
      toast({
        title: "PDF Test Failed",
        description: "PDF generation is not working. Using fallback method.",
        variant: "destructive",
      });
    }
  };

  // Add email functionality
  const sendEmail = async (booking: MockTestBooking) => {
    try {
      if (!booking.studentEmail) {
        toast({
          title: "No Email Address",
          description: "Student email address is not available.",
          variant: "destructive",
        });
        return;
      }

      if (!booking.testResult) {
        toast({
          title: "No Test Results",
          description: "Test results are not available to send.",
          variant: "destructive",
        });
        return;
      }

      // Show loading toast
      toast({
        title: "Sending Email",
        description: "Sending test results to student...",
      });

      // Call the Cloud Function
      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const functions = getFunctions();
      const sendTestResultEmail = httpsCallable(functions, 'sendTestResultEmail');

      const result = await sendTestResultEmail({
        studentEmail: booking.studentEmail,
        studentName: booking.studentName,
        testResult: booking.testResult,
        instructorName: booking.instructorName,
        testDate: booking.testResult.testDate,
        testTime: booking.testResult.testTime,
        testCentre: booking.testResult.testCentre
      });

      toast({
        title: "Email Sent Successfully",
        description: `Test results have been sent to ${booking.studentEmail}`,
      });

    } catch (error: any) {
      console.error('Error sending email:', error);
      toast({
        title: "Email Error",
        description: error.message || "Failed to send email. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <DashboardLayout role="instructor">
      <div className="space-y-6 print-section">
        {/* Print Header - Only visible when printing */}
        <div className="hidden print:block text-center mb-6">
          <h1 className="text-2xl font-bold">Way2Learn Driving School</h1>
          <h2 className="text-xl font-semibold">Mock Test Results Report</h2>
          <p className="text-sm text-gray-600">
            Generated on {new Date().toLocaleDateString()} at {new Date().toLocaleTimeString()}
          </p>
        </div>
        
        <div className="flex items-center justify-between no-print">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Mock Test Results</h1>
            <p className="text-gray-600 mt-2">
              Manage and record mock test results for your students
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={testPDFGeneration}
              className="border-blue-600 text-blue-600 hover:bg-blue-50"
            >
              Test PDF
            </Button>
            <Button
              variant="outline"
              onClick={() => window.print()}
              className="border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              <FileText className="w-4 h-4 mr-2" />
              Print View
            </Button>
          </div>
        </div>

        {/* Search and Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center">
                <FileText className="h-8 w-8 text-blue-600" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-600">Total Bookings</p>
                  <p className="text-2xl font-bold text-gray-900">{mockTestBookings.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center">
                <Clock className="h-8 w-8 text-yellow-600" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-600">Pending</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {mockTestBookings.filter(b => b.status === 'pending').length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center">
                <CheckCircle className="h-8 w-8 text-green-600" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-600">Completed</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {mockTestBookings.filter(b => b.status === 'completed').length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center">
                <AlertCircle className="h-8 w-8 text-red-600" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-600">Pass Rate</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {(() => {
                      const completed = mockTestBookings.filter(b => b.status === 'completed');
                      const passed = completed.filter(b => b.testResult?.passed);
                      return completed.length > 0 ? Math.round((passed.length / completed.length) * 100) : 0;
                    })()}%
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <Card className="no-print">
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Search by student name, email, or phone..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </CardContent>
        </Card>

        {/* Bookings Table */}
        <Card>
          <CardHeader>
            <CardTitle>Mock Test Bookings</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : filteredBookings.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No mock test bookings</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {searchQuery ? "No bookings match your search." : "No students have booked mock tests yet."}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Student Information</TableHead>
                      <TableHead>Test Details</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Result</TableHead>
                      <TableHead className="no-print">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredBookings.map((booking) => (
                      <TableRow 
                        key={booking.id}
                        className="hover:bg-gray-50"
                      >
                        <TableCell 
                          className="cursor-pointer"
                          onClick={() => {
                            if (booking.status === 'pending') {
                              handleAddResult(booking);
                            } else if (booking.status === 'completed') {
                              setSelectedBooking(booking);
                              if (booking.testResult) {
                                setTestResult({
                                  score: booking.testResult.score || 0,
                                  totalQuestions: booking.testResult.totalQuestions || 50,
                                  passed: booking.testResult.passed || false,
                                  notes: booking.testResult.notes || "",
                                  // New comprehensive fields
                                  testDate: booking.testResult.testDate || new Date().toISOString().split('T')[0],
                                  testTime: booking.testResult.testTime || new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }),
                                  testCentre: booking.testResult.testCentre || "",
                                  weather: booking.testResult.weather || "",
                                  faults: booking.testResult.faults || {} as Record<string, { df: number; s: number; d: number }>,
                                  debriefNotes: booking.testResult.debriefNotes || "",
                                  examinerSignature: booking.testResult.examinerSignature || ""
                                });
                              }
                              setResultDialogOpen(true);
                            }
                          }}
                        >
                          <div className="space-y-1">
                            <div className="font-medium text-gray-900">{booking.studentName}</div>
                            <div className="text-sm text-gray-500">{booking.studentEmail}</div>
                            <div className="text-sm text-gray-500 flex items-center">
                              <Phone className="w-3 h-3 mr-1" />
                              {booking.studentPhone}
                            </div>
                            <div className="text-sm text-gray-500 flex items-center">
                              <MapPin className="w-3 h-3 mr-1" />
                              {booking.studentPostcode}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell 
                          className="cursor-pointer"
                          onClick={() => {
                            if (booking.status === 'pending') {
                              handleAddResult(booking);
                            } else if (booking.status === 'completed') {
                              setSelectedBooking(booking);
                              if (booking.testResult) {
                                setTestResult({
                                  score: booking.testResult.score || 0,
                                  totalQuestions: booking.testResult.totalQuestions || 50,
                                  passed: booking.testResult.passed || false,
                                  notes: booking.testResult.notes || "",
                                  // New comprehensive fields
                                  testDate: booking.testResult.testDate || new Date().toISOString().split('T')[0],
                                  testTime: booking.testResult.testTime || new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }),
                                  testCentre: booking.testResult.testCentre || "",
                                  weather: booking.testResult.weather || "",
                                  faults: booking.testResult.faults || {} as Record<string, { df: number; s: number; d: number }>,
                                  debriefNotes: booking.testResult.debriefNotes || "",
                                  examinerSignature: booking.testResult.examinerSignature || ""
                                });
                              }
                              setResultDialogOpen(true);
                            }
                          }}
                        >
                          <div className="space-y-1">
                            <div className="text-sm font-medium text-gray-900">{booking.courseName}</div>
                            <div className="text-sm text-gray-500 flex items-center">
                              <Calendar className="w-3 h-3 mr-1" />
                              {new Date(booking.date).toLocaleDateString()}
                            </div>
                            <div className="text-sm text-gray-500">{booking.time}</div>
                          </div>
                        </TableCell>
                        <TableCell 
                          className="cursor-pointer"
                          onClick={() => {
                            if (booking.status === 'pending') {
                              handleAddResult(booking);
                            } else if (booking.status === 'completed') {
                              setSelectedBooking(booking);
                              if (booking.testResult) {
                                setTestResult({
                                  score: booking.testResult.score || 0,
                                  totalQuestions: booking.testResult.totalQuestions || 50,
                                  passed: booking.testResult.passed || false,
                                  notes: booking.testResult.notes || "",
                                  // New comprehensive fields
                                  testDate: booking.testResult.testDate || new Date().toISOString().split('T')[0],
                                  testTime: booking.testResult.testTime || new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }),
                                  testCentre: booking.testResult.testCentre || "",
                                  weather: booking.testResult.weather || "",
                                  faults: booking.testResult.faults || {} as Record<string, { df: number; s: number; d: number }>,
                                  debriefNotes: booking.testResult.debriefNotes || "",
                                  examinerSignature: booking.testResult.examinerSignature || ""
                                });
                              }
                              setResultDialogOpen(true);
                            }
                          }}
                        >
                          {getStatusBadge(booking.status)}
                        </TableCell>
                        <TableCell 
                          className="cursor-pointer"
                          onClick={() => {
                            if (booking.status === 'pending') {
                              handleAddResult(booking);
                            } else if (booking.status === 'completed') {
                              setSelectedBooking(booking);
                              if (booking.testResult) {
                                setTestResult({
                                  score: booking.testResult.score || 0,
                                  totalQuestions: booking.testResult.totalQuestions || 50,
                                  passed: booking.testResult.passed || false,
                                  notes: booking.testResult.notes || "",
                                  // New comprehensive fields
                                  testDate: booking.testResult.testDate || new Date().toISOString().split('T')[0],
                                  testTime: booking.testResult.testTime || new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }),
                                  testCentre: booking.testResult.testCentre || "",
                                  weather: booking.testResult.weather || "",
                                  faults: booking.testResult.faults || {} as Record<string, { df: number; s: number; d: number }>,
                                  debriefNotes: booking.testResult.debriefNotes || "",
                                  examinerSignature: booking.testResult.examinerSignature || ""
                                });
                              }
                              setResultDialogOpen(true);
                            }
                          }}
                        >
                          {booking.testResult ? (
                            <div className="space-y-1">
                              {getResultBadge(booking.testResult)}
                              <div className="text-xs text-gray-600 space-y-1">
                                {booking.testResult.faults && (
                                  <>
                                    <div>DF: {Object.values(booking.testResult.faults).reduce((sum, fault) => sum + (fault.df || 0), 0)}</div>
                                    <div>S: {Object.values(booking.testResult.faults).reduce((sum, fault) => sum + (fault.s || 0), 0)}</div>
                                    <div>D: {Object.values(booking.testResult.faults).reduce((sum, fault) => sum + (fault.d || 0), 0)}</div>
                                    {(() => {
                                      const totalDF = Object.values(booking.testResult.faults).reduce((sum, fault) => sum + (fault.df || 0), 0);
                                      const totalS = Object.values(booking.testResult.faults).reduce((sum, fault) => sum + (fault.s || 0), 0);
                                      const totalD = Object.values(booking.testResult.faults).reduce((sum, fault) => sum + (fault.d || 0), 0);
                                      if (totalDF >= 4) {
                                        return <div className="text-red-600 font-medium">DF ≥ 4 = FAIL</div>;
                                      } else if (totalS > 0) {
                                        return <div className="text-red-600 font-medium">S &gt; 0 = FAIL</div>;
                                      } else if (totalD > 0) {
                                        return <div className="text-red-600 font-medium">D &gt; 0 = FAIL</div>;
                                      }
                                      return null;
                                    })()}
                                  </>
                                )}
                                {booking.testResult.testCentre && (
                                  <div className="text-xs text-gray-500">
                                    {booking.testResult.testCentre}
                                  </div>
                                )}
                              </div>
                            </div>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </TableCell>
                        <TableCell className="no-print">
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAddResult(booking);
                              }}
                              className="bg-blue-600 hover:bg-blue-700"
                            >
                              {booking.testResult ? 'Edit Result' : 'Add Result'}
                            </Button>
                            {booking.testResult && (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    try {
                                      generatePDF(booking);
                                    } catch (error) {
                                      console.error('Main PDF generation failed, using fallback:', error);
                                      generatePDFFallback(booking);
                                    }
                                  }}
                                  className="border-green-600 text-green-600 hover:bg-green-50"
                                >
                                  <Download className="w-4 h-4 mr-1" />
                                  Download PDF
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    generateWord(booking);
                                  }}
                                  className="border-blue-600 text-blue-600 hover:bg-blue-50"
                                >
                                  <FileDown className="w-4 h-4 mr-1" />
                                  Download Word
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    sendEmail(booking);
                                  }}
                                  className="border-purple-600 text-purple-600 hover:bg-purple-50"
                                >
                                  <Mail className="w-4 h-4 mr-1" />
                                  Send Email
                                </Button>
                              </>
                            )}
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteBooking(booking);
                              }}
                              className="bg-red-600 hover:bg-red-700"
                            >
                              Delete
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Comprehensive Driving Test Marking Sheet Dialog */}
        <Dialog open={resultDialogOpen} onOpenChange={setResultDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-center">
                Way2Learn Driving School - Driving Test Marking Sheet
              </DialogTitle>
              <DialogDescription className="text-center">
                📞 020 3488 9598 | 🌐 www.way2learndrivingschool.com
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-6">
              {/* Candidate Details */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-lg mb-3">Candidate Details</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="candidateName">Name:</Label>
                    <Input
                      id="candidateName"
                      value={selectedBooking?.studentName || ''}
                      disabled
                      className="bg-white"
                    />
                  </div>
                  <div>
                    <Label htmlFor="testDate">Date:</Label>
                    <Input
                      id="testDate"
                      type="date"
                      value={testResult.testDate || new Date().toISOString().split('T')[0]}
                      onChange={(e) => setTestResult(prev => ({
                        ...prev,
                        testDate: e.target.value
                      }))}
                    />
                  </div>
                  <div>
                    <Label htmlFor="testTime">Time:</Label>
                    <Input
                      id="testTime"
                      type="time"
                      value={testResult.testTime || new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                      onChange={(e) => setTestResult(prev => ({
                        ...prev,
                        testTime: e.target.value
                      }))}
                    />
                  </div>
                  <div>
                    <Label htmlFor="instructor">Instructor:</Label>
                    <Input
                      id="instructor"
                      value={selectedBooking?.instructorName || ''}
                      disabled
                      className="bg-white"
                    />
                  </div>
                  <div>
                    <Label htmlFor="testCentre">Test Centre:</Label>
                    <Input
                      id="testCentre"
                      value={testResult.testCentre || ''}
                      onChange={(e) => setTestResult(prev => ({
                        ...prev,
                        testCentre: e.target.value
                      }))}
                      placeholder="Enter test centre"
                    />
                  </div>
                  <div>
                    <Label htmlFor="weather">Weather Conditions:</Label>
                    <select
                      id="weather"
                      value={testResult.weather || ''}
                      onChange={(e) => setTestResult(prev => ({
                        ...prev,
                        weather: e.target.value
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select weather</option>
                      <option value="Sunny">Sunny</option>
                      <option value="Cloudy">Cloudy</option>
                      <option value="Rainy">Rainy</option>
                      <option value="Foggy">Foggy</option>
                      <option value="Snowy">Snowy</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Fault Marking Table */}
              <div className="bg-white border rounded-lg overflow-hidden">
                <div className="bg-gray-100 p-3 border-b">
                  <h3 className="font-semibold">Fault Marking</h3>
                  <p className="text-sm text-gray-600">
                    Use the columns to mark the number of Driving Faults (DF), Serious Faults (S), or Dangerous Faults (D) observed during the test.
                  </p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left font-medium">Competency</th>
                        <th className="px-4 py-2 text-center font-medium">DF</th>
                        <th className="px-4 py-2 text-center font-medium">S</th>
                        <th className="px-4 py-2 text-center font-medium">D</th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* Eyesight and Basic Controls */}
                      <tr className="bg-blue-50">
                        <td colSpan={4} className="px-4 py-2 font-semibold text-blue-800">Eyesight and Basic Controls</td>
                      </tr>
                      {[
                        'Eyesight Check', 'Controlled Stop'
                      ].map((competency, index) => (
                        <tr key={`basic-${index}`} className="border-b hover:bg-gray-50">
                          <td className="px-4 py-2 text-sm">{competency}</td>
                          <td className="px-4 py-2 text-center">
                            <Input
                              type="number"
                              min="0"
                              max="10"
                              className="w-16 text-center"
                              value={testResult.faults?.[competency]?.df || 0}
                              onChange={(e) => setTestResult(prev => ({
                                ...prev,
                                faults: {
                                  ...prev.faults,
                                  [competency]: {
                                    ...prev.faults?.[competency],
                                    df: parseInt(e.target.value) || 0
                                  }
                                }
                              }))}
                            />
                          </td>
                          <td className="px-4 py-2 text-center">
                            <Input
                              type="number"
                              min="0"
                              max="3"
                              className="w-16 text-center"
                              value={testResult.faults?.[competency]?.s || 0}
                              onChange={(e) => setTestResult(prev => ({
                                ...prev,
                                faults: {
                                  ...prev.faults,
                                  [competency]: {
                                    ...prev.faults?.[competency],
                                    s: parseInt(e.target.value) || 0
                                  }
                                }
                              }))}
                            />
                          </td>
                          <td className="px-4 py-2 text-center">
                            <Input
                              type="number"
                              min="0"
                              max="3"
                              className="w-16 text-center"
                              value={testResult.faults?.[competency]?.d || 0}
                              onChange={(e) => setTestResult(prev => ({
                                ...prev,
                                faults: {
                                  ...prev.faults,
                                  [competency]: {
                                    ...prev.faults?.[competency],
                                    d: parseInt(e.target.value) || 0
                                  }
                                }
                              }))}
                            />
                          </td>
                        </tr>
                      ))}

                      {/* Moving Off */}
                      <tr className="bg-green-50">
                        <td colSpan={4} className="px-4 py-2 font-semibold text-green-800">Moving Off</td>
                      </tr>
                      {[
                        'Move off - Safety', 'Move off - Control'
                      ].map((competency, index) => (
                        <tr key={`moveoff-${index}`} className="border-b hover:bg-gray-50">
                          <td className="px-4 py-2 text-sm">{competency}</td>
                          <td className="px-4 py-2 text-center">
                            <Input
                              type="number"
                              min="0"
                              max="10"
                              className="w-16 text-center"
                              value={testResult.faults?.[competency]?.df || 0}
                              onChange={(e) => setTestResult(prev => ({
                                ...prev,
                                faults: {
                                  ...prev.faults,
                                  [competency]: {
                                    ...prev.faults?.[competency],
                                    df: parseInt(e.target.value) || 0
                                  }
                                }
                              }))}
                            />
                          </td>
                          <td className="px-4 py-2 text-center">
                            <Input
                              type="number"
                              min="0"
                              max="3"
                              className="w-16 text-center"
                              value={testResult.faults?.[competency]?.s || 0}
                              onChange={(e) => setTestResult(prev => ({
                                ...prev,
                                faults: {
                                  ...prev.faults,
                                  [competency]: {
                                    ...prev.faults?.[competency],
                                    s: parseInt(e.target.value) || 0
                                  }
                                }
                              }))}
                            />
                          </td>
                          <td className="px-4 py-2 text-center">
                            <Input
                              type="number"
                              min="0"
                              max="3"
                              className="w-16 text-center"
                              value={testResult.faults?.[competency]?.d || 0}
                              onChange={(e) => setTestResult(prev => ({
                                ...prev,
                                faults: {
                                  ...prev.faults,
                                  [competency]: {
                                    ...prev.faults?.[competency],
                                    d: parseInt(e.target.value) || 0
                                  }
                                }
                              }))}
                            />
                          </td>
                        </tr>
                      ))}

                      {/* Use of Mirrors */}
                      <tr className="bg-purple-50">
                        <td colSpan={4} className="px-4 py-2 font-semibold text-purple-800">Use of Mirrors</td>
                      </tr>
                      {[
                        'Use of mirrors - Change direction', 'Use of mirrors - Change speed', 'Use of mirrors signalling'
                      ].map((competency, index) => (
                        <tr key={`mirrors-${index}`} className="border-b hover:bg-gray-50">
                          <td className="px-4 py-2 text-sm">{competency}</td>
                          <td className="px-4 py-2 text-center">
                            <Input
                              type="number"
                              min="0"
                              max="10"
                              className="w-16 text-center"
                              value={testResult.faults?.[competency]?.df || 0}
                              onChange={(e) => setTestResult(prev => ({
                                ...prev,
                                faults: {
                                  ...prev.faults,
                                  [competency]: {
                                    ...prev.faults?.[competency],
                                    df: parseInt(e.target.value) || 0
                                  }
                                }
                              }))}
                            />
                          </td>
                          <td className="px-4 py-2 text-center">
                            <Input
                              type="number"
                              min="0"
                              max="3"
                              className="w-16 text-center"
                              value={testResult.faults?.[competency]?.s || 0}
                              onChange={(e) => setTestResult(prev => ({
                                ...prev,
                                faults: {
                                  ...prev.faults,
                                  [competency]: {
                                    ...prev.faults?.[competency],
                                    s: parseInt(e.target.value) || 0
                                  }
                                }
                              }))}
                            />
                          </td>
                          <td className="px-4 py-2 text-center">
                            <Input
                              type="number"
                              min="0"
                              max="3"
                              className="w-16 text-center"
                              value={testResult.faults?.[competency]?.d || 0}
                              onChange={(e) => setTestResult(prev => ({
                                ...prev,
                                faults: {
                                  ...prev.faults,
                                  [competency]: {
                                    ...prev.faults?.[competency],
                                    d: parseInt(e.target.value) || 0
                                  }
                                }
                              }))}
                            />
                          </td>
                        </tr>
                      ))}

                      {/* Signals */}
                      <tr className="bg-orange-50">
                        <td colSpan={4} className="px-4 py-2 font-semibold text-orange-800">Signals</td>
                      </tr>
                      {[
                        'Signals - Necessary', 'Signals - Correctly', 'Signal timed'
                      ].map((competency, index) => (
                        <tr key={`signals-${index}`} className="border-b hover:bg-gray-50">
                          <td className="px-4 py-2 text-sm">{competency}</td>
                          <td className="px-4 py-2 text-center">
                            <Input
                              type="number"
                              min="0"
                              max="10"
                              className="w-16 text-center"
                              value={testResult.faults?.[competency]?.df || 0}
                              onChange={(e) => setTestResult(prev => ({
                                ...prev,
                                faults: {
                                  ...prev.faults,
                                  [competency]: {
                                    ...prev.faults?.[competency],
                                    df: parseInt(e.target.value) || 0
                                  }
                                }
                              }))}
                            />
                          </td>
                          <td className="px-4 py-2 text-center">
                            <Input
                              type="number"
                              min="0"
                              max="3"
                              className="w-16 text-center"
                              value={testResult.faults?.[competency]?.s || 0}
                              onChange={(e) => setTestResult(prev => ({
                                ...prev,
                                faults: {
                                  ...prev.faults,
                                  [competency]: {
                                    ...prev.faults?.[competency],
                                    s: parseInt(e.target.value) || 0
                                  }
                                }
                              }))}
                            />
                          </td>
                          <td className="px-4 py-2 text-center">
                            <Input
                              type="number"
                              min="0"
                              max="3"
                              className="w-16 text-center"
                              value={testResult.faults?.[competency]?.d || 0}
                              onChange={(e) => setTestResult(prev => ({
                                ...prev,
                                faults: {
                                  ...prev.faults,
                                  [competency]: {
                                    ...prev.faults?.[competency],
                                    d: parseInt(e.target.value) || 0
                                  }
                                }
                              }))}
                            />
                          </td>
                        </tr>
                      ))}

                      {/* Clearance and Following Distance */}
                      <tr className="bg-yellow-50">
                        <td colSpan={4} className="px-4 py-2 font-semibold text-yellow-800">Clearance and Following Distance</td>
                      </tr>
                      {[
                        'Clearance / Obstructions', 'Following distance'
                      ].map((competency, index) => (
                        <tr key={`clearance-${index}`} className="border-b hover:bg-gray-50">
                          <td className="px-4 py-2 text-sm">{competency}</td>
                          <td className="px-4 py-2 text-center">
                            <Input
                              type="number"
                              min="0"
                              max="10"
                              className="w-16 text-center"
                              value={testResult.faults?.[competency]?.df || 0}
                              onChange={(e) => setTestResult(prev => ({
                                ...prev,
                                faults: {
                                  ...prev.faults,
                                  [competency]: {
                                    ...prev.faults?.[competency],
                                    df: parseInt(e.target.value) || 0
                                  }
                                }
                              }))}
                            />
                          </td>
                          <td className="px-4 py-2 text-center">
                            <Input
                              type="number"
                              min="0"
                              max="3"
                              className="w-16 text-center"
                              value={testResult.faults?.[competency]?.s || 0}
                              onChange={(e) => setTestResult(prev => ({
                                ...prev,
                                faults: {
                                  ...prev.faults,
                                  [competency]: {
                                    ...prev.faults?.[competency],
                                    s: parseInt(e.target.value) || 0
                                  }
                                }
                              }))}
                            />
                          </td>
                          <td className="px-4 py-2 text-center">
                            <Input
                              type="number"
                              min="0"
                              max="3"
                              className="w-16 text-center"
                              value={testResult.faults?.[competency]?.d || 0}
                              onChange={(e) => setTestResult(prev => ({
                                ...prev,
                                faults: {
                                  ...prev.faults,
                                  [competency]: {
                                    ...prev.faults?.[competency],
                                    d: parseInt(e.target.value) || 0
                                  }
                                }
                              }))}
                            />
                          </td>
                        </tr>
                      ))}

                      {/* Response to Signs and Signals */}
                      <tr className="bg-indigo-50">
                        <td colSpan={4} className="px-4 py-2 font-semibold text-indigo-800">Response to Signs and Signals</td>
                      </tr>
                      {[
                        'Response to signs - Traffic signs', 'Response to signs - Road markings',
                        'Response to signals - Traffic lights'
                      ].map((competency, index) => (
                        <tr key={`response-${index}`} className="border-b hover:bg-gray-50">
                          <td className="px-4 py-2 text-sm">{competency}</td>
                          <td className="px-4 py-2 text-center">
                            <Input
                              type="number"
                              min="0"
                              max="10"
                              className="w-16 text-center"
                              value={testResult.faults?.[competency]?.df || 0}
                              onChange={(e) => setTestResult(prev => ({
                                ...prev,
                                faults: {
                                  ...prev.faults,
                                  [competency]: {
                                    ...prev.faults?.[competency],
                                    df: parseInt(e.target.value) || 0
                                  }
                                }
                              }))}
                            />
                          </td>
                          <td className="px-4 py-2 text-center">
                            <Input
                              type="number"
                              min="0"
                              max="3"
                              className="w-16 text-center"
                              value={testResult.faults?.[competency]?.s || 0}
                              onChange={(e) => setTestResult(prev => ({
                                ...prev,
                                faults: {
                                  ...prev.faults,
                                  [competency]: {
                                    ...prev.faults?.[competency],
                                    s: parseInt(e.target.value) || 0
                                  }
                                }
                              }))}
                            />
                          </td>
                          <td className="px-4 py-2 text-center">
                            <Input
                              type="number"
                              min="0"
                              max="3"
                              className="w-16 text-center"
                              value={testResult.faults?.[competency]?.d || 0}
                              onChange={(e) => setTestResult(prev => ({
                                ...prev,
                                faults: {
                                  ...prev.faults,
                                  [competency]: {
                                    ...prev.faults?.[competency],
                                    d: parseInt(e.target.value) || 0
                                  }
                                }
                              }))}
                            />
                          </td>
                        </tr>
                      ))}

                      {/* Junctions */}
                      <tr className="bg-pink-50">
                        <td colSpan={4} className="px-4 py-2 font-semibold text-pink-800">Junctions</td>
                      </tr>
                      {[
                        'Junctions - Approach speed', 'Junctions - Observation'
                      ].map((competency, index) => (
                        <tr key={`junctions-${index}`} className="border-b hover:bg-gray-50">
                          <td className="px-4 py-2 text-sm">{competency}</td>
                          <td className="px-4 py-2 text-center">
                            <Input
                              type="number"
                              min="0"
                              max="10"
                              className="w-16 text-center"
                              value={testResult.faults?.[competency]?.df || 0}
                              onChange={(e) => setTestResult(prev => ({
                                ...prev,
                                faults: {
                                  ...prev.faults,
                                  [competency]: {
                                    ...prev.faults?.[competency],
                                    df: parseInt(e.target.value) || 0
                                  }
                                }
                              }))}
                            />
                          </td>
                          <td className="px-4 py-2 text-center">
                            <Input
                              type="number"
                              min="0"
                              max="3"
                              className="w-16 text-center"
                              value={testResult.faults?.[competency]?.s || 0}
                              onChange={(e) => setTestResult(prev => ({
                                ...prev,
                                faults: {
                                  ...prev.faults,
                                  [competency]: {
                                    ...prev.faults?.[competency],
                                    s: parseInt(e.target.value) || 0
                                  }
                                }
                              }))}
                            />
                          </td>
                          <td className="px-4 py-2 text-center">
                            <Input
                              type="number"
                              min="0"
                              max="3"
                              className="w-16 text-center"
                              value={testResult.faults?.[competency]?.d || 0}
                              onChange={(e) => setTestResult(prev => ({
                                ...prev,
                                faults: {
                                  ...prev.faults,
                                  [competency]: {
                                    ...prev.faults?.[competency],
                                    d: parseInt(e.target.value) || 0
                                  }
                                }
                              }))}
                            />
                          </td>
                        </tr>
                      ))}

                      {/* Judgement */}
                      <tr className="bg-red-50">
                        <td colSpan={4} className="px-4 py-2 font-semibold text-red-800">Judgement</td>
                      </tr>
                      {[
                        'Judgement - Meeting', 'Judgement - Crossing'
                      ].map((competency, index) => (
                        <tr key={`judgement-${index}`} className="border-b hover:bg-gray-50">
                          <td className="px-4 py-2 text-sm">{competency}</td>
                          <td className="px-4 py-2 text-center">
                            <Input
                              type="number"
                              min="0"
                              max="10"
                              className="w-16 text-center"
                              value={testResult.faults?.[competency]?.df || 0}
                              onChange={(e) => setTestResult(prev => ({
                                ...prev,
                                faults: {
                                  ...prev.faults,
                                  [competency]: {
                                    ...prev.faults?.[competency],
                                    df: parseInt(e.target.value) || 0
                                  }
                                }
                              }))}
                            />
                          </td>
                          <td className="px-4 py-2 text-center">
                            <Input
                              type="number"
                              min="0"
                              max="3"
                              className="w-16 text-center"
                              value={testResult.faults?.[competency]?.s || 0}
                              onChange={(e) => setTestResult(prev => ({
                                ...prev,
                                faults: {
                                  ...prev.faults,
                                  [competency]: {
                                    ...prev.faults?.[competency],
                                    s: parseInt(e.target.value) || 0
                                  }
                                }
                              }))}
                            />
                          </td>
                          <td className="px-4 py-2 text-center">
                            <Input
                              type="number"
                              min="0"
                              max="3"
                              className="w-16 text-center"
                              value={testResult.faults?.[competency]?.d || 0}
                              onChange={(e) => setTestResult(prev => ({
                                ...prev,
                                faults: {
                                  ...prev.faults,
                                  [competency]: {
                                    ...prev.faults?.[competency],
                                    d: parseInt(e.target.value) || 0
                                  }
                                }
                              }))}
                            />
                          </td>
                        </tr>
                      ))}

                      {/* Positioning */}
                      <tr className="bg-teal-50">
                        <td colSpan={4} className="px-4 py-2 font-semibold text-teal-800">Positioning</td>
                      </tr>
                      {[
                        'Positioning - Normal driving', 'Positioning - Lane discipline'
                      ].map((competency, index) => (
                        <tr key={`positioning-${index}`} className="border-b hover:bg-gray-50">
                          <td className="px-4 py-2 text-sm">{competency}</td>
                          <td className="px-4 py-2 text-center">
                            <Input
                              type="number"
                              min="0"
                              max="10"
                              className="w-16 text-center"
                              value={testResult.faults?.[competency]?.df || 0}
                              onChange={(e) => setTestResult(prev => ({
                                ...prev,
                                faults: {
                                  ...prev.faults,
                                  [competency]: {
                                    ...prev.faults?.[competency],
                                    df: parseInt(e.target.value) || 0
                                  }
                                }
                              }))}
                            />
                          </td>
                          <td className="px-4 py-2 text-center">
                            <Input
                              type="number"
                              min="0"
                              max="3"
                              className="w-16 text-center"
                              value={testResult.faults?.[competency]?.s || 0}
                              onChange={(e) => setTestResult(prev => ({
                                ...prev,
                                faults: {
                                  ...prev.faults,
                                  [competency]: {
                                    ...prev.faults?.[competency],
                                    s: parseInt(e.target.value) || 0
                                  }
                                }
                              }))}
                            />
                          </td>
                          <td className="px-4 py-2 text-center">
                            <Input
                              type="number"
                              min="0"
                              max="3"
                              className="w-16 text-center"
                              value={testResult.faults?.[competency]?.d || 0}
                              onChange={(e) => setTestResult(prev => ({
                                ...prev,
                                faults: {
                                  ...prev.faults,
                                  [competency]: {
                                    ...prev.faults?.[competency],
                                    d: parseInt(e.target.value) || 0
                                  }
                                }
                              }))}
                            />
                          </td>
                        </tr>
                      ))}

                      {/* Pedestrian Crossings and Stops */}
                      <tr className="bg-cyan-50">
                        <td colSpan={4} className="px-4 py-2 font-semibold text-cyan-800">Pedestrian Crossings and Stops</td>
                      </tr>
                      {[
                        'Pedestrian crossings', 'Position / Normal stops'
                      ].map((competency, index) => (
                        <tr key={`pedestrian-${index}`} className="border-b hover:bg-gray-50">
                          <td className="px-4 py-2 text-sm">{competency}</td>
                          <td className="px-4 py-2 text-center">
                            <Input
                              type="number"
                              min="0"
                              max="10"
                              className="w-16 text-center"
                              value={testResult.faults?.[competency]?.df || 0}
                              onChange={(e) => setTestResult(prev => ({
                                ...prev,
                                faults: {
                                  ...prev.faults,
                                  [competency]: {
                                    ...prev.faults?.[competency],
                                    df: parseInt(e.target.value) || 0
                                  }
                                }
                              }))}
                            />
                          </td>
                          <td className="px-4 py-2 text-center">
                            <Input
                              type="number"
                              min="0"
                              max="3"
                              className="w-16 text-center"
                              value={testResult.faults?.[competency]?.s || 0}
                              onChange={(e) => setTestResult(prev => ({
                                ...prev,
                                faults: {
                                  ...prev.faults,
                                  [competency]: {
                                    ...prev.faults?.[competency],
                                    s: parseInt(e.target.value) || 0
                                  }
                                }
                              }))}
                            />
                          </td>
                          <td className="px-4 py-2 text-center">
                            <Input
                              type="number"
                              min="0"
                              max="3"
                              className="w-16 text-center"
                              value={testResult.faults?.[competency]?.d || 0}
                              onChange={(e) => setTestResult(prev => ({
                                ...prev,
                                faults: {
                                  ...prev.faults,
                                  [competency]: {
                                    ...prev.faults?.[competency],
                                    d: parseInt(e.target.value) || 0
                                  }
                                }
                              }))}
                            />
                          </td>
                        </tr>
                      ))}

                      {/* Vehicle Control */}
                      <tr className="bg-lime-50">
                        <td colSpan={4} className="px-4 py-2 font-semibold text-lime-800">Vehicle Control</td>
                      </tr>
                      {[
                        'Control - Steering', 'Control - Gears', 'Control - Accelerator',
                        'Control - Footbrake', 'Control - Clutch'
                      ].map((competency, index) => (
                        <tr key={`control-${index}`} className="border-b hover:bg-gray-50">
                          <td className="px-4 py-2 text-sm">{competency}</td>
                          <td className="px-4 py-2 text-center">
                            <Input
                              type="number"
                              min="0"
                              max="10"
                              className="w-16 text-center"
                              value={testResult.faults?.[competency]?.df || 0}
                              onChange={(e) => setTestResult(prev => ({
                                ...prev,
                                faults: {
                                  ...prev.faults,
                                  [competency]: {
                                    ...prev.faults?.[competency],
                                    df: parseInt(e.target.value) || 0
                                  }
                                }
                              }))}
                            />
                          </td>
                          <td className="px-4 py-2 text-center">
                            <Input
                              type="number"
                              min="0"
                              max="3"
                              className="w-16 text-center"
                              value={testResult.faults?.[competency]?.s || 0}
                              onChange={(e) => setTestResult(prev => ({
                                ...prev,
                                faults: {
                                  ...prev.faults,
                                  [competency]: {
                                    ...prev.faults?.[competency],
                                    s: parseInt(e.target.value) || 0
                                  }
                                }
                              }))}
                            />
                          </td>
                          <td className="px-4 py-2 text-center">
                            <Input
                              type="number"
                              min="0"
                              max="3"
                              className="w-16 text-center"
                              value={testResult.faults?.[competency]?.d || 0}
                              onChange={(e) => setTestResult(prev => ({
                                ...prev,
                                faults: {
                                  ...prev.faults,
                                  [competency]: {
                                    ...prev.faults?.[competency],
                                    d: parseInt(e.target.value) || 0
                                  }
                                }
                              }))}
                            />
                          </td>
                        </tr>
                      ))}

                      {/* Use of Speed */}
                      <tr className="bg-amber-50">
                        <td colSpan={4} className="px-4 py-2 font-semibold text-amber-800">Use of Speed</td>
                      </tr>
                      {[
                        'Use of speed'
                      ].map((competency, index) => (
                        <tr key={`speed-${index}`} className="border-b hover:bg-gray-50">
                          <td className="px-4 py-2 text-sm">{competency}</td>
                          <td className="px-4 py-2 text-center">
                            <Input
                              type="number"
                              min="0"
                              max="10"
                              className="w-16 text-center"
                              value={testResult.faults?.[competency]?.df || 0}
                              onChange={(e) => setTestResult(prev => ({
                                ...prev,
                                faults: {
                                  ...prev.faults,
                                  [competency]: {
                                    ...prev.faults?.[competency],
                                    df: parseInt(e.target.value) || 0
                                  }
                                }
                              }))}
                            />
                          </td>
                          <td className="px-4 py-2 text-center">
                            <Input
                              type="number"
                              min="0"
                              max="3"
                              className="w-16 text-center"
                              value={testResult.faults?.[competency]?.s || 0}
                              onChange={(e) => setTestResult(prev => ({
                                ...prev,
                                faults: {
                                  ...prev.faults,
                                  [competency]: {
                                    ...prev.faults?.[competency],
                                    s: parseInt(e.target.value) || 0
                                  }
                                }
                              }))}
                            />
                          </td>
                          <td className="px-4 py-2 text-center">
                            <Input
                              type="number"
                              min="0"
                              max="3"
                              className="w-16 text-center"
                              value={testResult.faults?.[competency]?.d || 0}
                              onChange={(e) => setTestResult(prev => ({
                                ...prev,
                                faults: {
                                  ...prev.faults,
                                  [competency]: {
                                    ...prev.faults?.[competency],
                                    d: parseInt(e.target.value) || 0
                                  }
                                }
                              }))}
                            />
                          </td>
                        </tr>
                      ))}

                      {/* Progress */}
                      <tr className="bg-emerald-50">
                        <td colSpan={4} className="px-4 py-2 font-semibold text-emerald-800">Progress</td>
                      </tr>
                      {[
                        'Progress - Appropriate', 'Progress - Speed', 'Progress - Under hesitation'
                      ].map((competency, index) => (
                        <tr key={`progress-${index}`} className="border-b hover:bg-gray-50">
                          <td className="px-4 py-2 text-sm">{competency}</td>
                          <td className="px-4 py-2 text-center">
                            <Input
                              type="number"
                              min="0"
                              max="10"
                              className="w-16 text-center"
                              value={testResult.faults?.[competency]?.df || 0}
                              onChange={(e) => setTestResult(prev => ({
                                ...prev,
                                faults: {
                                  ...prev.faults,
                                  [competency]: {
                                    ...prev.faults?.[competency],
                                    df: parseInt(e.target.value) || 0
                                  }
                                }
                              }))}
                            />
                          </td>
                          <td className="px-4 py-2 text-center">
                            <Input
                              type="number"
                              min="0"
                              max="3"
                              className="w-16 text-center"
                              value={testResult.faults?.[competency]?.s || 0}
                              onChange={(e) => setTestResult(prev => ({
                                ...prev,
                                faults: {
                                  ...prev.faults,
                                  [competency]: {
                                    ...prev.faults?.[competency],
                                    s: parseInt(e.target.value) || 0
                                  }
                                }
                              }))}
                            />
                          </td>
                          <td className="px-4 py-2 text-center">
                            <Input
                              type="number"
                              min="0"
                              max="3"
                              className="w-16 text-center"
                              value={testResult.faults?.[competency]?.d || 0}
                              onChange={(e) => setTestResult(prev => ({
                                ...prev,
                                faults: {
                                  ...prev.faults,
                                  [competency]: {
                                    ...prev.faults?.[competency],
                                    d: parseInt(e.target.value) || 0
                                  }
                                }
                              }))}
                            />
                          </td>
                        </tr>
                      ))}

                      {/* Emergency Stop and Parking */}
                      <tr className="bg-rose-50">
                        <td colSpan={4} className="px-4 py-2 font-semibold text-rose-800">Emergency Stop and Parking</td>
                      </tr>
                      {[
                        'Emergency stop', 'Reverse park - Road', 'Reverse park - Bay', 'Forward park',
                        'Reverse / Right'
                      ].map((competency, index) => (
                        <tr key={`parking-${index}`} className="border-b hover:bg-gray-50">
                          <td className="px-4 py-2 text-sm">{competency}</td>
                          <td className="px-4 py-2 text-center">
                            <Input
                              type="number"
                              min="0"
                              max="10"
                              className="w-16 text-center"
                              value={testResult.faults?.[competency]?.df || 0}
                              onChange={(e) => setTestResult(prev => ({
                                ...prev,
                                faults: {
                                  ...prev.faults,
                                  [competency]: {
                                    ...prev.faults?.[competency],
                                    df: parseInt(e.target.value) || 0
                                  }
                                }
                              }))}
                            />
                          </td>
                          <td className="px-4 py-2 text-center">
                            <Input
                              type="number"
                              min="0"
                              max="3"
                              className="w-16 text-center"
                              value={testResult.faults?.[competency]?.s || 0}
                              onChange={(e) => setTestResult(prev => ({
                                ...prev,
                                faults: {
                                  ...prev.faults,
                                  [competency]: {
                                    ...prev.faults?.[competency],
                                    s: parseInt(e.target.value) || 0
                                  }
                                }
                              }))}
                            />
                          </td>
                          <td className="px-4 py-2 text-center">
                            <Input
                              type="number"
                              min="0"
                              max="3"
                              className="w-16 text-center"
                              value={testResult.faults?.[competency]?.d || 0}
                              onChange={(e) => setTestResult(prev => ({
                                ...prev,
                                faults: {
                                  ...prev.faults,
                                  [competency]: {
                                    ...prev.faults?.[competency],
                                    d: parseInt(e.target.value) || 0
                                  }
                                }
                              }))}
                            />
                          </td>
                        </tr>
                      ))}

                      {/* Show Me Tell Me */}
                      <tr className="bg-violet-50">
                        <td colSpan={4} className="px-4 py-2 font-semibold text-violet-800">Show Me Tell Me</td>
                      </tr>
                      {[
                        'Show Me / Tell Me'
                      ].map((competency, index) => (
                        <tr key={`showme-${index}`} className="border-b hover:bg-gray-50">
                          <td className="px-4 py-2 text-sm">{competency}</td>
                          <td className="px-4 py-2 text-center">
                            <Input
                              type="number"
                              min="0"
                              max="10"
                              className="w-16 text-center"
                              value={testResult.faults?.[competency]?.df || 0}
                              onChange={(e) => setTestResult(prev => ({
                                ...prev,
                                faults: {
                                  ...prev.faults,
                                  [competency]: {
                                    ...prev.faults?.[competency],
                                    df: parseInt(e.target.value) || 0
                                  }
                                }
                              }))}
                            />
                          </td>
                          <td className="px-4 py-2 text-center">
                            <Input
                              type="number"
                              min="0"
                              max="3"
                              className="w-16 text-center"
                              value={testResult.faults?.[competency]?.s || 0}
                              onChange={(e) => setTestResult(prev => ({
                                ...prev,
                                faults: {
                                  ...prev.faults,
                                  [competency]: {
                                    ...prev.faults?.[competency],
                                    s: parseInt(e.target.value) || 0
                                  }
                                }
                              }))}
                            />
                          </td>
                          <td className="px-4 py-2 text-center">
                            <Input
                              type="number"
                              min="0"
                              max="3"
                              className="w-16 text-center"
                              value={testResult.faults?.[competency]?.d || 0}
                              onChange={(e) => setTestResult(prev => ({
                                ...prev,
                                faults: {
                                  ...prev.faults,
                                  [competency]: {
                                    ...prev.faults?.[competency],
                                    d: parseInt(e.target.value) || 0
                                  }
                                }
                              }))}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Debrief Notes */}
              <div>
                <Label htmlFor="debriefNotes">Debrief Notes</Label>
                <Textarea
                  id="debriefNotes"
                  placeholder="Enter detailed debrief notes about the test performance..."
                  value={testResult.debriefNotes || ''}
                  onChange={(e) => setTestResult(prev => ({
                    ...prev,
                    debriefNotes: e.target.value
                  }))}
                  rows={4}
                />
              </div>

              {/* Test Result Summary */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="font-semibold mb-3">Test Result Summary</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Total Driving Faults (DF):</Label>
                    <div className={`text-lg font-bold ${(() => {
                      const totalDF = Object.values(testResult.faults || {}).reduce((sum, fault) => sum + (fault.df || 0), 0);
                      return totalDF >= 4 ? 'text-red-600' : 'text-blue-600';
                    })()}`}>
                      {Object.values(testResult.faults || {}).reduce((sum, fault) => sum + (fault.df || 0), 0)}
                      {(() => {
                        const totalDF = Object.values(testResult.faults || {}).reduce((sum, fault) => sum + (fault.df || 0), 0);
                        return totalDF >= 4 ? ' (FAIL - Too many DF)' : '';
                      })()}
                    </div>
                  </div>
                  <div>
                    <Label>Total Serious Faults (S):</Label>
                    <div className={`text-lg font-bold ${(() => {
                      const totalS = Object.values(testResult.faults || {}).reduce((sum, fault) => sum + (fault.s || 0), 0);
                      return totalS > 0 ? 'text-red-600' : 'text-orange-600';
                    })()}`}>
                      {Object.values(testResult.faults || {}).reduce((sum, fault) => sum + (fault.s || 0), 0)}
                      {(() => {
                        const totalS = Object.values(testResult.faults || {}).reduce((sum, fault) => sum + (fault.s || 0), 0);
                        return totalS > 0 ? ' (FAIL - Serious fault)' : '';
                      })()}
                    </div>
                  </div>
                  <div>
                    <Label>Total Dangerous Faults (D):</Label>
                    <div className={`text-lg font-bold ${(() => {
                      const totalD = Object.values(testResult.faults || {}).reduce((sum, fault) => sum + (fault.d || 0), 0);
                      return totalD > 0 ? 'text-red-600' : 'text-red-600';
                    })()}`}>
                      {Object.values(testResult.faults || {}).reduce((sum, fault) => sum + (fault.d || 0), 0)}
                      {(() => {
                        const totalD = Object.values(testResult.faults || {}).reduce((sum, fault) => sum + (fault.d || 0), 0);
                        return totalD > 0 ? ' (FAIL - Dangerous fault)' : '';
                      })()}
                    </div>
                  </div>
                  <div>
                    <Label>Test Result:</Label>
                    <div className="text-lg font-bold">
                      {(() => {
                        const seriousFaults = Object.values(testResult.faults || {}).reduce((sum, fault) => sum + (fault.s || 0), 0);
                        const dangerousFaults = Object.values(testResult.faults || {}).reduce((sum, fault) => sum + (fault.d || 0), 0);
                        const drivingFaults = Object.values(testResult.faults || {}).reduce((sum, fault) => sum + (fault.df || 0), 0);
                        return seriousFaults === 0 && dangerousFaults === 0 && drivingFaults < 4 ? (
                          <span className="text-green-600">PASS</span>
                        ) : (
                          <span className="text-red-600">FAIL</span>
                        );
                      })()}
                    </div>
                  </div>
                </div>
                {/* Pass/Fail Rules */}
                <div className="mt-4 p-3 bg-gray-100 rounded text-sm">
                  <div className="font-semibold mb-2">Pass/Fail Rules:</div>
                  <ul className="space-y-1">
                    <li>• <span className="text-red-600 font-medium">FAIL</span> if any Serious Faults (S) &gt; 0</li>
                    <li>• <span className="text-red-600 font-medium">FAIL</span> if any Dangerous Faults (D) &gt; 0</li>
                    <li>• <span className="text-red-600 font-medium">FAIL</span> if Driving Faults (DF) ≥ 4</li>
                    <li>• <span className="text-green-600 font-medium">PASS</span> only if S = 0, D = 0, and DF &lt; 4</li>
                  </ul>
                </div>
              </div>
            </div>
            
            <DialogFooter>
              <div className="flex gap-2">
                {selectedBooking?.testResult && (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => {
                        try {
                          generatePDF(selectedBooking);
                        } catch (error) {
                          console.error('Main PDF generation failed, using fallback:', error);
                          generatePDFFallback(selectedBooking);
                        }
                      }}
                      className="border-green-600 text-green-600 hover:bg-green-50"
                    >
                      <Download className="w-4 h-4 mr-1" />
                      Download PDF
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => generateWord(selectedBooking)}
                      className="border-blue-600 text-blue-600 hover:bg-blue-50"
                    >
                      <FileDown className="w-4 h-4 mr-1" />
                      Download Word
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => sendEmail(selectedBooking)}
                      className="border-purple-600 text-purple-600 hover:bg-purple-50"
                    >
                      <Mail className="w-4 h-4 mr-1" />
                      Send Email
                    </Button>
                  </>
                )}
                <Button variant="outline" onClick={() => setResultDialogOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleSaveResult}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {selectedBooking?.testResult ? 'Update Result' : 'Save Result'}
                </Button>
              </div>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default MockTestResults; 